// Die Sätze der Prüfliste. Reiner Oberflächentext: der Server schickt zu jeder Zeile `label` und `severity`
// (services/queue.ts, KIND_META), aber weder einen Satz, der sagt was falsch ist, noch einen, der sagt was es
// kostet, noch welche Aktion sie auflöst. Diese Datei ist keine zweite Regelimplementierung — sie entscheidet
// nichts, sie erklärt. Die Zuordnung Art → Aktion beschreibt nur, welcher bestehende Endpunkt angeboten wird.

/** Jede Aktion entspricht genau einem Endpunkt (oder einem Verweis auf einen anderen Bildschirm). */
export type PruefAktion =
  | 'produkt_zuweisen' // PATCH /zahlungen/:cinId {manual_product_id}
  | 'abschluss_zuweisen' // PATCH /zahlungen/:cinId {manual_deal_id}
  | 'abschluss_anlegen' // POST  /zahlungen/:cinId/create-deal
  | 'zahlung_ignorieren' // PATCH /zahlungen/:cinId {ignored_reason}
  | 'datum_setzen' // PATCH /zahlungen/:cinId {delta_id, paid_at}
  | 'netto_brutto' // PATCH /zahlungen/:cinId {delta_id, net_cents, gross_cents}
  | 'rueckzahlung' // POST  /zahlungen/:cinId/refund/:deltaId/confirm | ignore
  | 'zahlung_erfassen' // POST  /zahlungen/manuell
  | 'rolle_zuweisen' // PATCH /abschluesse/:dealId {closer_tc_id | setter_tc_id | host_tc_id}
  | 'abschluss_produkt' // PATCH /abschluesse/:dealId {product_id}
  | 'abschluss_status' // PATCH /abschluesse/:dealId {status}
  | 'abschluesse_vergleichen' // POST /abschluesse/:behalten/zusammenfuehren | POST /inbox/doppelter_abschluss/:id/dismiss
  | 'zur_rechnung' // Verweis auf /t/:id/rechnungen
  | 'zu_den_regeln' // Verweis auf /t/:id/regeln
  | 'zu_den_einstellungen' // Verweis auf /t/:id/einstellungen
  | 'erledigen' // POST /inbox/:kind/:subjectId/dismiss

export interface PruefKindText {
  /** Kurzüberschrift der Zeile; deckungsgleich mit KIND_META.label des Servers. */
  titel: string
  /** Was ist falsch — ein Satz, kein Fachbegriff. */
  satz: string
  /** Was es kostet, solange es offen bleibt. */
  kosten: string
  /** Beschriftung des betroffenen Betrags, oder null wenn kein Geld am Fall hängt. */
  betragLabel: string | null
  aktionen: PruefAktion[]
}

const ERLEDIGEN: PruefAktion = 'erledigen'

export const PRUEF_KINDS: Readonly<Record<string, PruefKindText | undefined>> = {
  webhook_fehler: {
    titel: 'Webhook fehlgeschlagen',
    satz: 'Eine Lieferung aus FinSuites ließ sich dreimal nicht verarbeiten und wird nicht erneut versucht.',
    kosten: 'Die Zahlung aus dieser Lieferung ist nirgends verbucht: keine Rate, keine Provision, kein Saldo.',
    betragLabel: null,
    aktionen: ['zahlung_erfassen', ERLEDIGEN],
  },
  unparseable: {
    titel: 'Zahlung nicht lesbar',
    satz: 'Betrag oder Datum dieser Zahlung stehen in einem Format, das nicht eindeutig lesbar ist; sie wurde deshalb nicht übernommen.',
    kosten: 'Aus dieser Zahlung entsteht keine Provision, bis sie von Hand erfasst ist.',
    betragLabel: 'Gemeldeter Betrag',
    aktionen: ['zahlung_erfassen', ERLEDIGEN],
  },
  refund_candidate: {
    titel: 'Rückzahlung prüfen',
    satz: 'FinSuites meldet für diese Rechnung weniger bezahlt, als bisher verbucht ist. Das System entscheidet darüber nicht selbst.',
    kosten:
      'Bestätigen Sie, sinkt der verbuchte Zahlungsstand und die Provisionen darauf werden zurückgebucht. Ignorieren Sie, bleibt alles wie verbucht.',
    betragLabel: 'Rückzahlung',
    aktionen: ['rueckzahlung'],
  },
  rechnung_rueckbuchung: {
    titel: 'Rechnung enthält Rückbuchung',
    satz: 'Auf dieser eingereichten Abrechnung steht eine Provisionszeile, die das System inzwischen zurückgebucht hat.',
    kosten:
      'Eine Freigabe würde einen Betrag festschreiben, von dem das System schon weiß, dass er nicht mehr stimmt. Bitte ablehnen statt freigeben.',
    betragLabel: 'Rechnungsbetrag netto',
    aktionen: ['zur_rechnung', ERLEDIGEN],
  },
  netto_brutto_unplausibel: {
    titel: 'Netto/Brutto unplausibel',
    satz: 'Netto und Brutto dieser Zahlung passen nicht zusammen, deshalb konnte keine Provisionsbasis gebildet werden.',
    kosten: 'Auf dieser Rate entsteht keine einzige Provision, bis die beiden Beträge korrigiert sind.',
    betragLabel: 'Zahlungseingang',
    aktionen: ['netto_brutto'],
  },
  'match:kein_produkt': {
    titel: 'Kein Produkt erkannt',
    satz: 'Aus dem Rechnungsnamen ließ sich kein Produkt erkennen; damit ist auch kein Abschluss zuzuordnen.',
    kosten: 'Ohne Produkt greift keine Provisionsregel: aus dieser Zahlung entsteht nichts.',
    betragLabel: 'Verbucht',
    aktionen: ['produkt_zuweisen', 'abschluss_zuweisen', 'zahlung_ignorieren'],
  },
  'match:kein_match': {
    titel: 'Kein passender Abschluss',
    satz: 'Zu Kontakt und Produkt dieser Zahlung gibt es keinen Abschluss.',
    kosten: 'Ohne Abschluss gibt es keine Beteiligten und keine Provision aus dieser Zahlung.',
    betragLabel: 'Verbucht',
    aktionen: ['abschluss_zuweisen', 'abschluss_anlegen', 'zahlung_ignorieren'],
  },
  'match:kein_aktiver_abschluss': {
    titel: 'Kein aktiver Abschluss',
    satz: 'Es gibt einen Abschluss zu Kontakt und Produkt, aber keinen mit Status „aktiv“ oder „Weiterlauf“.',
    kosten: 'Solange kein aktiver Abschluss passt, bleibt die Zahlung unzugeordnet und provisionsfrei.',
    betragLabel: 'Verbucht',
    aktionen: ['abschluss_zuweisen', 'zahlung_ignorieren'],
  },
  'match:mehrere_abschluesse': {
    titel: 'Mehrere aktive Abschlüsse',
    satz: 'Mehrere aktive Abschlüsse passen auf Kontakt und Produkt. Welcher gemeint ist, entscheidet das System nicht.',
    kosten: 'Bis die Zuordnung feststeht, entsteht aus dieser Zahlung keine Provision.',
    betragLabel: 'Verbucht',
    aktionen: ['abschluss_zuweisen', 'zahlung_ignorieren'],
  },
  'match:kontakt_fehlt': {
    titel: 'Kontakt-ID fehlt (Zahlung)',
    satz: 'Zu dieser Zahlung fehlt die FinSuites-Kontakt-ID, über die Zahlungen sonst ihren Abschluss finden.',
    kosten: 'Ohne Kontakt-ID findet die automatische Zuordnung nichts; die Zahlung bleibt provisionsfrei.',
    betragLabel: 'Verbucht',
    aktionen: ['abschluss_zuweisen', 'abschluss_anlegen', 'zahlung_ignorieren'],
  },
  'match:manuell_pruefen': {
    titel: 'Manuelle Zuordnung prüfen',
    satz: 'Die manuell hinterlegte Abschluss-Zuordnung dieser Zahlung zeigt auf keinen Abschluss dieses Auftraggebers.',
    kosten: 'Die Zahlung hängt an einer Zuordnung, die ins Leere geht; es entsteht keine Provision.',
    betragLabel: 'Verbucht',
    aktionen: ['abschluss_zuweisen', 'zahlung_ignorieren'],
  },
  datum_fehlt: {
    titel: 'Bezahlt am fehlt',
    satz: 'Zu dieser Rate fehlt der Zahlungseingang („Bezahlt am“).',
    kosten:
      'Provisionen aus dieser Rate lassen sich nicht abrechnen: einer Rechnung fehlt sonst das Leistungsdatum (§ 14 Abs. 4 Nr. 6 UStG).',
    betragLabel: 'Zahlungseingang',
    aktionen: ['datum_setzen'],
  },
  doppelter_abschluss: {
    titel: 'Doppelter Abschluss',
    satz: 'Zwei aktive Abschlüsse haben denselben Kontakt und dasselbe Produkt — meist kam derselbe Abschluss zweimal aus FinSuites, mit abweichendem Namen oder Wert.',
    kosten: 'Solange beide aktiv sind, lässt sich keine neue Zahlung dieses Kunden zuordnen. „Vergleichen“ zeigt beide nebeneinander: einen behalten und den anderen streichen, oder beide behalten.',
    betragLabel: 'Vertragswert',
    aktionen: ['abschluesse_vergleichen', 'abschluss_status', ERLEDIGEN],
  },
  rolle_fehlt: {
    titel: 'Rolle fehlt',
    satz: 'Zu dieser Provisionszeile ist am Abschluss niemand für die Rolle eingetragen.',
    kosten: 'Die Zeile zählt in keinem Saldo und kann von niemandem abgerechnet werden.',
    betragLabel: 'Provisionsbetrag',
    aktionen: ['rolle_zuweisen'],
  },
  produkt_fehlt: {
    titel: 'Produkt fehlt (Abschluss)',
    satz: 'Diesem Abschluss ist kein Produkt zugeordnet.',
    kosten: 'Ohne Produkt greift keine Regel: Zahlungen auf diesen Abschluss erzeugen keine Provision.',
    betragLabel: 'Vertragswert',
    aktionen: ['abschluss_produkt'],
  },
  kontakt_fehlt: {
    titel: 'Kontakt-ID fehlt (Abschluss)',
    satz: 'Diesem Abschluss fehlt die FinSuites-Kontakt-ID.',
    kosten:
      'Zahlungen dieses Kunden finden den Abschluss nicht von selbst und müssen jedes Mal von Hand zugeordnet werden.',
    betragLabel: 'Vertragswert',
    aktionen: [ERLEDIGEN],
  },
  abschluss_pruefen: {
    titel: 'Abschluss prüfen',
    satz: 'Dieser Abschluss steht auf Status „prüfen“; aus FinSuites kam ein Status, der keinem bekannten entspricht.',
    kosten: 'Ein Abschluss auf „prüfen“ ist nicht aktiv: neue Zahlungen finden ihn bei der Zuordnung nicht.',
    betragLabel: 'Vertragswert',
    aktionen: ['abschluss_status'],
  },
  produkt_ohne_regel: {
    titel: 'Produkt ohne Regel',
    satz: 'Für dieses Produkt gibt es keine gültige Provisionsregel.',
    kosten:
      'Jede Zahlung auf dieses Produkt wird ordentlich verbucht und erzeugt trotzdem keine einzige Provision.',
    betragLabel: null,
    aktionen: ['zu_den_regeln', ERLEDIGEN],
  },
  net_source_subtotal: {
    titel: 'Steuerfeld fehlt im Zahlungs-Webhook',
    satz: 'Der Zahlungs-Webhook liefert kein Steuerfeld, deshalb wird als Netto der Rechnungs-Zwischenbetrag verwendet.',
    kosten: 'Rabatte auf der Kundenrechnung senken die Provisionsbasis nicht — die Provisionen fallen zu hoch aus.',
    betragLabel: null,
    aktionen: ['zu_den_einstellungen', ERLEDIGEN],
  },
}

/**
 * Eigenschaftszugriff über Object.hasOwn, aus demselben Grund wie `lookupKind` im Server: `PRUEF_KINDS['toString']`
 * wäre über die Prototypkette nicht undefined und würde eine Funktion als Text durchreichen.
 */
export function pruefKind(kind: string): PruefKindText {
  const gefunden = Object.hasOwn(PRUEF_KINDS, kind) ? PRUEF_KINDS[kind] : undefined
  if (gefunden !== undefined) return gefunden
  return {
    titel: 'Prüfen',
    satz: `Diese Prüfung (${kind}) kennt die Oberfläche noch nicht.`,
    kosten: 'Was sie kostet, steht hier noch nicht. Bitte im Audit-Log nachsehen.',
    betragLabel: null,
    aktionen: ['erledigen'],
  }
}
